import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Link,
} from "@mui/material";
import {
  Download,
  Public,
  CalendarMonth,
  TableChart,
} from "@mui/icons-material";
import * as df from "date-fns";

// ── Types ─────────────────────────────────────────────────────────────

interface SortieRow {
  _id: string;
  callsign: string;
  platform: string;
  launchBase: string;
  takeoff: string | null;
  landing: string | null;
  onStation: string | null;
  offStation: string | null;
  operatingArea: string | null;
  int_prime: string | null;
  armed: boolean;
  hold: boolean;
  canx: boolean;
  taskingCount: number;
}

interface ScheduleRow {
  callsign: string;
  platform: string;
  launchBase: string;
  takeoff: string | null;
  onStation: string | null;
  offStation: string | null;
  landing: string | null;
  operatingArea: string | null;
  int_prime: string | null;
  int_secondary: string | null;
  ato_day: string | null;
  ato_mission_number: string | null;
  armed: boolean;
  hold: boolean;
  canx: boolean;
}

interface CollectionDeck {
  callsign: string;
  operatingArea: string | null;
  deckName: string;
  deckCategory: string;
  deckType: string;
  intDiscipline: string | null;
  priority: string | null;
  crType: string | null;
  targetId: string | null;
  targetName: string | null;
  coordinates: string | null;
}

interface RstaRow {
  callsign: string;
  operatingArea: string | null;
  platform: string;
  intPrime: string | null;
  takeoff: string | null;
  landing: string | null;
  targetId: string | null;
  targetName: string | null;
  coordinates: string | null;
  priority: string | null;
}

interface ChangeLogEntry {
  callsign: string;
  operatingArea: string | null;
  type: string;
  reason: string | null;
  justification: string | null;
  author: string;
  at: string;
}

type ExportKey =
  | "schedule-weekly"
  | "schedule-custom"
  | "kml-date"
  | "kml-mission"
  | "sortie-summary"
  | "collection-decks"
  | "rsta-html"
  | "rsta-csv"
  | "change-log";

// ── Constants / styles ────────────────────────────────────────────────

const DEMO_DAY = "2024-05-07"; // seeded demo ATO day

const paperSx = {
  bgcolor: "#1a1c26",
  border: "1px solid #333",
  borderRadius: 1,
} as const;

const headCellSx = {
  color: "#8a90a0",
  borderBottom: "1px solid #333",
  fontWeight: "bold",
  whiteSpace: "nowrap",
  fontSize: 12,
} as const;

const bodyCellSx = {
  color: "#e0e0e0",
  borderBottom: "1px solid #2a2c36",
  whiteSpace: "nowrap",
  fontSize: 13,
} as const;

const fieldSx = {
  "& .MuiInputBase-root": { bgcolor: "#12141c" },
  minWidth: 170,
} as const;

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return df.format(d, "dd MMM HH:mm'Z'");
}

function StatusChips({ armed, hold, canx }: { armed: boolean; hold: boolean; canx: boolean }) {
  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {armed && (
        <Chip label="ARMED" size="small" sx={{ bgcolor: "#f72bef22", color: "#f72bef", fontSize: 10, height: 18 }} />
      )}
      {hold && (
        <Chip label="HOLD" size="small" sx={{ bgcolor: "#ff910022", color: "#ff9100", fontSize: 10, height: 18 }} />
      )}
      {canx && (
        <Chip label="CANX" size="small" sx={{ bgcolor: "#ff174422", color: "#ff1744", fontSize: 10, height: 18 }} />
      )}
      {!armed && !hold && !canx && <Typography sx={{ color: "#555", fontSize: 12 }}>—</Typography>}
    </Box>
  );
}

/** Client-side CSV download built from the sortie summary JSON rows. */
function downloadSortieCsv(rows: SortieRow[], date: string) {
  const headers = [
    "callsign", "platform", "launchBase", "takeoff", "landing", "onStation",
    "offStation", "operatingArea", "int_prime", "armed", "hold", "canx", "taskingCount",
  ];
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => esc((r as unknown as Record<string, unknown>)[h])).join(",")),
  ].join("\r\n");
  const a = document.createElement("a");
  a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
  a.download = `sortie-summary-${date}.csv`;
  a.click();
}

// ── Component ─────────────────────────────────────────────────────────

export default function Exports() {
  const [selection, setSelection] = useState<ExportKey>("sortie-summary");
  const [date, setDate] = useState(DEMO_DAY);
  const [customStart, setCustomStart] = useState(DEMO_DAY);
  const [customEnd, setCustomEnd] = useState(
    df.format(df.addDays(new Date(DEMO_DAY + "T00:00:00Z"), 2), "yyyy-MM-dd")
  );
  const [missionId, setMissionId] = useState("");

  // Change log date range: default to last 30 days ending today
  const [changeLogStart, setChangeLogStart] = useState(
    df.format(df.subDays(new Date(DEMO_DAY + "T00:00:00Z"), 29), "yyyy-MM-dd")
  );
  const [changeLogEnd, setChangeLogEnd] = useState(DEMO_DAY);

  // Default every date control to the latest ATO day that has missions.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/main/dates")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d?.latest) return;
        const latest = new Date(d.latest + "T00:00:00Z");
        setDate(d.latest);
        setCustomStart(d.latest);
        setCustomEnd(df.format(df.addDays(latest, 2), "yyyy-MM-dd"));
        setChangeLogStart(df.format(df.subDays(latest, 29), "yyyy-MM-dd"));
        setChangeLogEnd(d.latest);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const [sortieRows, setSortieRows] = useState<SortieRow[]>([]);
  const [scheduleRows, setScheduleRows] = useState<ScheduleRow[]>([]);
  const [collectionDecks, setCollectionDecks] = useState<CollectionDeck[]>([]);
  const [rstaRows, setRstaRows] = useState<RstaRow[]>([]);
  const [changeLogRows, setChangeLogRows] = useState<ChangeLogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  // Weekly range: the week containing the anchor date
  const weekStart = useMemo(
    () => df.format(df.startOfWeek(new Date(date + "T00:00:00Z"), { weekStartsOn: 1 }), "yyyy-MM-dd"),
    [date]
  );
  const weekEnd = useMemo(
    () => df.format(df.endOfWeek(new Date(date + "T00:00:00Z"), { weekStartsOn: 1 }), "yyyy-MM-dd"),
    [date]
  );

  const scheduleRange: [string, string] =
    selection === "schedule-weekly" ? [weekStart, weekEnd] : [customStart, customEnd];

  // ── Data fetches (all public reads) ────────────────────────────────
  const loadSortie = useCallback(() => {
    setLoading(true);
    fetch(`/api/sortie-summary?date=${date}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setSortieRows(Array.isArray(rows) ? rows : []))
      .catch(() => setSortieRows([]))
      .finally(() => setLoading(false));
  }, [date]);

  const loadSchedule = useCallback(() => {
    const [start, end] = selection === "schedule-weekly" ? [weekStart, weekEnd] : [customStart, customEnd];
    setLoading(true);
    fetch(`/api/export/schedule?start=${start}&end=${end}&format=json`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setScheduleRows(Array.isArray(rows) ? rows : []))
      .catch(() => setScheduleRows([]))
      .finally(() => setLoading(false));
  }, [selection, weekStart, weekEnd, customStart, customEnd]);

  const loadCollectionDecks = useCallback(() => {
    setLoading(true);
    fetch(`/api/export/collection-decks?date=${date}&format=json`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setCollectionDecks(Array.isArray(rows) ? rows : []))
      .catch(() => setCollectionDecks([]))
      .finally(() => setLoading(false));
  }, [date]);

  const loadRstaRows = useCallback(() => {
    setLoading(true);
    fetch(`/api/export/collection-decks?date=${date}&format=json`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setRstaRows(Array.isArray(rows) ? rows : []))
      .catch(() => setRstaRows([]))
      .finally(() => setLoading(false));
  }, [date]);

  const loadChangeLog = useCallback(() => {
    setLoading(true);
    fetch(`/api/export/change-log?start=${changeLogStart}&end=${changeLogEnd}&format=json`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setChangeLogRows(Array.isArray(rows) ? rows : []))
      .catch(() => setChangeLogRows([]))
      .finally(() => setLoading(false));
  }, [changeLogStart, changeLogEnd]);

  useEffect(() => {
    // Sortie rows also drive the KML previews (mission list + counts)
    if (selection === "sortie-summary" || selection === "kml-date" || selection === "kml-mission") {
      loadSortie();
    } else if (selection === "schedule-weekly" || selection === "schedule-custom") {
      loadSchedule();
    } else if (selection === "collection-decks") {
      loadCollectionDecks();
    } else if (selection === "rsta-html" || selection === "rsta-csv") {
      loadRstaRows();
    } else if (selection === "change-log") {
      loadChangeLog();
    }
  }, [selection, loadSortie, loadSchedule, loadCollectionDecks, loadRstaRows, loadChangeLog]);

  // Default the mission selector to the first mission of the day
  useEffect(() => {
    if (selection === "kml-mission" && !missionId && sortieRows.length > 0) {
      setMissionId(sortieRows[0]._id);
    }
  }, [selection, missionId, sortieRows]);

  // ── Left panel entries ─────────────────────────────────────────────
  const categories: Array<{ label: string; items: Array<{ key: ExportKey; label: string; sub: string }> }> = [
    {
      label: "SCHEDULE",
      items: [
        { key: "schedule-weekly", label: "Weekly Schedule", sub: "Missions for the ATO week (CSV)" },
        { key: "schedule-custom", label: "Custom Range Schedule", sub: "Missions between two dates (CSV)" },
      ],
    },
    {
      label: "COLLECTION DETAILS",
      items: [
        { key: "kml-date", label: "Targets KML by Date", sub: "All mission targets for a date" },
        { key: "kml-mission", label: "Targets KML by Mission", sub: "Targets for one mission" },
        { key: "collection-decks", label: "Collection Decks", sub: "Deck inventory by date (CSV)" },
      ],
    },
    {
      label: "RSTA",
      items: [
        { key: "rsta-html", label: "RSTA Words (HTML)", sub: "Printable RSTA annex document" },
        { key: "rsta-csv", label: "RSTA Sheet (CSV)", sub: "RSTA data from collection decks" },
      ],
    },
    {
      label: "EXECUTION",
      items: [
        { key: "sortie-summary", label: "Sortie Summary", sub: "One row per sortie for a date" },
        { key: "change-log", label: "Sync & Change Log", sub: "Configuration changes over time (CSV)" },
      ],
    },
  ];

  const selectedMission = sortieRows.find((r) => r._id === missionId);

  // ── Render helpers ─────────────────────────────────────────────────

  const renderDatePicker = (label: string, value: string, onChange: (v: string) => void) => (
    <TextField
      label={label}
      type="date"
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputLabelProps={{ shrink: true }}
      sx={fieldSx}
    />
  );

  const renderSortiePreview = () => (
    <>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
        {renderDatePicker("Mission Date", date, setDate)}
        <Button
          variant="outlined"
          size="small"
          startIcon={<Download />}
          disabled={sortieRows.length === 0}
          onClick={() => downloadSortieCsv(sortieRows, date)}
          sx={{ color: "#f72bef", borderColor: "#f72bef" }}
        >
          Download CSV
        </Button>
        <Typography sx={{ color: "#8a90a0", fontSize: 13 }}>
          {sortieRows.length} sortie{sortieRows.length === 1 ? "" : "s"}
        </Typography>
      </Box>
      <TableContainer component={Paper} sx={{ ...paperSx, maxHeight: "60vh" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["Callsign", "Platform", "Launch Base", "Takeoff", "On Station", "Off Station", "Landing", "Op Area", "INT", "Status", "Taskings"].map((h) => (
                <TableCell key={h} sx={{ ...headCellSx, bgcolor: "#12141c" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortieRows.map((r) => (
              <TableRow key={r._id} hover>
                <TableCell sx={{ ...bodyCellSx, color: "#f72bef", fontWeight: "bold" }}>{r.callsign}</TableCell>
                <TableCell sx={bodyCellSx}>{r.platform}</TableCell>
                <TableCell sx={bodyCellSx}>{r.launchBase}</TableCell>
                <TableCell sx={bodyCellSx}>{fmtTime(r.takeoff)}</TableCell>
                <TableCell sx={bodyCellSx}>{fmtTime(r.onStation)}</TableCell>
                <TableCell sx={bodyCellSx}>{fmtTime(r.offStation)}</TableCell>
                <TableCell sx={bodyCellSx}>{fmtTime(r.landing)}</TableCell>
                <TableCell sx={bodyCellSx}>{r.operatingArea || "—"}</TableCell>
                <TableCell sx={bodyCellSx}>{r.int_prime || "—"}</TableCell>
                <TableCell sx={bodyCellSx}><StatusChips armed={r.armed} hold={r.hold} canx={r.canx} /></TableCell>
                <TableCell sx={bodyCellSx} align="center">{r.taskingCount}</TableCell>
              </TableRow>
            ))}
            {sortieRows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={11} sx={{ ...bodyCellSx, color: "#8a90a0", textAlign: "center" }}>
                  No missions found for {date}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const renderSchedulePreview = () => {
    const [start, end] = scheduleRange;
    const csvUrl = `/api/export/schedule?start=${start}&end=${end}&format=csv`;
    return (
      <>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          {selection === "schedule-weekly" ? (
            <>
              {renderDatePicker("Week Containing", date, setDate)}
              <Typography sx={{ color: "#8a90a0", fontSize: 13 }}>
                Week: {weekStart} → {weekEnd}
              </Typography>
            </>
          ) : (
            <>
              {renderDatePicker("Start", customStart, setCustomStart)}
              {renderDatePicker("End", customEnd, setCustomEnd)}
            </>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            href={csvUrl}
            sx={{ color: "#f72bef", borderColor: "#f72bef" }}
          >
            Download CSV
          </Button>
          <Typography sx={{ color: "#8a90a0", fontSize: 13 }}>
            {scheduleRows.length} mission{scheduleRows.length === 1 ? "" : "s"}
          </Typography>
        </Box>
        <TableContainer component={Paper} sx={{ ...paperSx, maxHeight: "60vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {["Callsign", "Platform", "Launch Base", "Takeoff", "On Station", "Off Station", "Landing", "Op Area", "INT / SEC", "ATO Day", "ATO Msn #", "Status"].map((h) => (
                  <TableCell key={h} sx={{ ...headCellSx, bgcolor: "#12141c" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduleRows.map((r, i) => (
                <TableRow key={`${r.callsign}-${i}`} hover>
                  <TableCell sx={{ ...bodyCellSx, color: "#f72bef", fontWeight: "bold" }}>{r.callsign}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.platform}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.launchBase}</TableCell>
                  <TableCell sx={bodyCellSx}>{fmtTime(r.takeoff)}</TableCell>
                  <TableCell sx={bodyCellSx}>{fmtTime(r.onStation)}</TableCell>
                  <TableCell sx={bodyCellSx}>{fmtTime(r.offStation)}</TableCell>
                  <TableCell sx={bodyCellSx}>{fmtTime(r.landing)}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.operatingArea || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    {r.int_prime || "—"}{r.int_secondary ? ` / ${r.int_secondary}` : ""}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>{r.ato_day || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.ato_mission_number || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}><StatusChips armed={r.armed} hold={r.hold} canx={r.canx} /></TableCell>
                </TableRow>
              ))}
              {scheduleRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={12} sx={{ ...bodyCellSx, color: "#8a90a0", textAlign: "center" }}>
                    No missions found between {start} and {end}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  const renderKmlPreview = () => {
    const byMission = selection === "kml-mission";
    const kmlUrl = byMission
      ? missionId ? `/api/mission/${missionId}/targets/kml` : ""
      : `/api/missions/${date}/targets/kml`;
    return (
      <Paper sx={{ ...paperSx, p: 3, maxWidth: 640 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          {renderDatePicker("Mission Date", date, setDate)}
          {byMission && (
            <FormControl size="small" sx={{ ...fieldSx, minWidth: 220 }}>
              <InputLabel sx={{ color: "#8a90a0" }}>Mission</InputLabel>
              <Select
                label="Mission"
                value={sortieRows.some((r) => r._id === missionId) ? missionId : ""}
                onChange={(e) => setMissionId(e.target.value)}
              >
                {sortieRows.map((r) => (
                  <MenuItem key={r._id} value={r._id}>
                    {r.callsign} — {r.platform}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Typography sx={{ color: "#e0e0e0", mb: 1, fontWeight: "bold" }}>
          {byMission
            ? selectedMission
              ? `Targets KML — ${selectedMission.callsign}`
              : "Targets KML — select a mission"
            : `Targets KML — all missions on ${date}`}
        </Typography>
        <Typography sx={{ color: "#8a90a0", fontSize: 13, mb: 2 }}>
          A Google Earth / TAK compatible KML document with one folder per mission
          callsign. Each folder contains a placemark for every target found in the
          mission&apos;s collection decks, taskings and NIB requirements (positioned
          from DMS coordinates), plus its takeoff and landing bases where the base
          ICAO is known.
        </Typography>
        <Typography sx={{ color: "#8a90a0", fontSize: 13, mb: 2 }}>
          {byMission
            ? selectedMission
              ? `${selectedMission.callsign} launches from ${selectedMission.launchBase} with ${selectedMission.taskingCount} tasking${selectedMission.taskingCount === 1 ? "" : "s"}.`
              : "No mission selected."
            : `${sortieRows.length} mission${sortieRows.length === 1 ? "" : "s"} on ${date} will be included.`}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Download />}
          href={kmlUrl}
          disabled={!kmlUrl}
          sx={{ color: "#f72bef", borderColor: "#f72bef" }}
        >
          Download KML
        </Button>
        {kmlUrl && (
          <Typography sx={{ color: "#555", fontSize: 12, mt: 1.5 }}>
            Direct link:{" "}
            <Link href={kmlUrl} sx={{ color: "#00b8ff" }}>
              {kmlUrl}
            </Link>
          </Typography>
        )}
      </Paper>
    );
  };

  const renderCollectionDecksPreview = () => {
    const totalCount = collectionDecks.length;
    const displayCount = Math.min(50, totalCount);
    const displayRows = collectionDecks.slice(0, displayCount);
    const csvUrl = `/api/export/collection-decks?date=${date}&format=csv`;
    return (
      <>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          {renderDatePicker("Deck Date", date, setDate)}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            href={csvUrl}
            sx={{ color: "#f72bef", borderColor: "#f72bef" }}
          >
            Download CSV
          </Button>
          <Typography sx={{ color: "#8a90a0", fontSize: 13 }}>
            {displayCount} of {totalCount} deck{totalCount === 1 ? "" : "s"}
          </Typography>
        </Box>
        <TableContainer component={Paper} sx={{ ...paperSx, maxHeight: "60vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {["Callsign", "Deck", "Category", "INT", "Priority", "Target", "Coordinates"].map((h) => (
                  <TableCell key={h} sx={{ ...headCellSx, bgcolor: "#12141c" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ ...bodyCellSx, color: "#f72bef", fontWeight: "bold" }}>{r.callsign}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.deckName || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.deckCategory || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.intDiscipline || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.priority || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.targetName || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.coordinates || "—"}</TableCell>
                </TableRow>
              ))}
              {totalCount === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ ...bodyCellSx, color: "#8a90a0", textAlign: "center" }}>
                    No decks found for {date}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  const renderRstaHtmlPreview = () => {
    const htmlUrl = `/api/export/rsta?date=${date}&format=html`;
    return (
      <Paper sx={{ ...paperSx, p: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          {renderDatePicker("RSTA Date", date, setDate)}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={() => window.open(htmlUrl, "_blank")}
            sx={{ color: "#f72bef", borderColor: "#f72bef" }}
          >
            Open Full Document
          </Button>
        </Box>
        <Typography sx={{ color: "#e0e0e0", mb: 2, fontWeight: "bold" }}>
          RSTA Words (HTML)
        </Typography>
        <Typography sx={{ color: "#8a90a0", fontSize: 13, mb: 2 }}>
          A printable RSTA annex document containing reconnaissance, surveillance, and target acquisition information for all missions on the selected date.
        </Typography>
        <Box sx={{ border: "1px solid #333", borderRadius: 1, overflow: "hidden", mb: 2 }}>
          <iframe
            src={htmlUrl}
            style={{
              width: "100%",
              height: 400,
              border: "none",
              backgroundColor: "white",
            }}
            title="RSTA Preview"
          />
        </Box>
      </Paper>
    );
  };

  const renderRstaCSVPreview = () => {
    const totalCount = rstaRows.length;
    const displayCount = Math.min(50, totalCount);
    const displayRows = rstaRows.slice(0, displayCount);
    const csvUrl = `/api/export/rsta?date=${date}&format=csv`;
    return (
      <>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          {renderDatePicker("RSTA Date", date, setDate)}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            href={csvUrl}
            sx={{ color: "#f72bef", borderColor: "#f72bef" }}
          >
            Download CSV
          </Button>
          <Typography sx={{ color: "#8a90a0", fontSize: 13 }}>
            {displayCount} of {totalCount} row{totalCount === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Typography sx={{ color: "#8a90a0", fontSize: 12, mb: 2, fontStyle: "italic" }}>
          Preview approximated from collection deck data
        </Typography>
        <TableContainer component={Paper} sx={{ ...paperSx, maxHeight: "60vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {["Callsign", "Platform", "Takeoff", "Landing", "Target", "Coordinates", "Priority"].map((h) => (
                  <TableCell key={h} sx={{ ...headCellSx, bgcolor: "#12141c" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ ...bodyCellSx, color: "#f72bef", fontWeight: "bold" }}>{r.callsign}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.platform || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{fmtTime(r.takeoff)}</TableCell>
                  <TableCell sx={bodyCellSx}>{fmtTime(r.landing)}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.targetName || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.coordinates || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.priority || "—"}</TableCell>
                </TableRow>
              ))}
              {totalCount === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ ...bodyCellSx, color: "#8a90a0", textAlign: "center" }}>
                    No RSTA data found for {date}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  const renderChangeLogPreview = () => {
    const totalCount = changeLogRows.length;
    const csvUrl = `/api/export/change-log?start=${changeLogStart}&end=${changeLogEnd}&format=csv`;
    return (
      <>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          {renderDatePicker("Start", changeLogStart, setChangeLogStart)}
          {renderDatePicker("End", changeLogEnd, setChangeLogEnd)}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            href={csvUrl}
            sx={{ color: "#f72bef", borderColor: "#f72bef" }}
          >
            Download CSV
          </Button>
          <Typography sx={{ color: "#8a90a0", fontSize: 13 }}>
            {totalCount} change{totalCount === 1 ? "" : "s"}
          </Typography>
        </Box>
        <TableContainer component={Paper} sx={{ ...paperSx, maxHeight: "60vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {["When", "Callsign", "Type", "Reason", "Justification", "Author"].map((h) => (
                  <TableCell key={h} sx={{ ...headCellSx, bgcolor: "#12141c" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {changeLogRows.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={bodyCellSx}>{fmtTime(r.at)}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: "#f72bef", fontWeight: "bold" }}>{r.callsign}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.type || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.reason || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.justification || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.author || "—"}</TableCell>
                </TableRow>
              ))}
              {totalCount === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ ...bodyCellSx, color: "#8a90a0", textAlign: "center" }}>
                    No changes found between {changeLogStart} and {changeLogEnd}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  const iconFor = (key: ExportKey) =>
    key.startsWith("kml") ? <Public sx={{ fontSize: 18 }} /> : key.startsWith("schedule") ? <CalendarMonth sx={{ fontSize: 18 }} /> : <TableChart sx={{ fontSize: 18 }} />;

  // ── Layout ─────────────────────────────────────────────────────────
  return (
    <Box>
      <Typography variant="h4" sx={{ color: "#f72bef", mb: 3, fontWeight: "bold" }}>
        Export Hub
      </Typography>
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* Left panel — exportable files */}
        <Paper sx={{ ...paperSx, width: 300, flexShrink: 0 }}>
          <Typography
            sx={{
              color: "#8a90a0",
              px: 2,
              py: 1.5,
              fontSize: 12,
              fontWeight: "bold",
              letterSpacing: 1,
              borderBottom: "1px solid #333",
            }}
          >
            EXPORTABLE FILES
          </Typography>
          <List dense disablePadding>
            {categories.map((cat) => (
              <React.Fragment key={cat.label}>
                <ListSubheader
                  sx={{ bgcolor: "transparent", color: "#f72bef", fontSize: 11, letterSpacing: 1, lineHeight: "32px" }}
                >
                  {cat.label}
                </ListSubheader>
                {cat.items.map((item) => (
                  <ListItemButton
                    key={item.key}
                    selected={selection === item.key}
                    onClick={() => setSelection(item.key)}
                    sx={{
                      "&.Mui-selected": { bgcolor: "#f72bef22", borderRight: "2px solid #f72bef" },
                      "&.Mui-selected:hover": { bgcolor: "#f72bef33" },
                    }}
                  >
                    <Box sx={{ color: selection === item.key ? "#f72bef" : "#8a90a0", mr: 1.5, display: "flex" }}>
                      {iconFor(item.key)}
                    </Box>
                    <ListItemText
                      primary={item.label}
                      secondary={item.sub}
                      primaryTypographyProps={{ sx: { color: "#e0e0e0", fontSize: 14 } }}
                      secondaryTypographyProps={{ sx: { color: "#666", fontSize: 11 } }}
                    />
                  </ListItemButton>
                ))}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {/* Right panel — preview */}
        <Box sx={{ flex: 1, minWidth: 360 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: "#f72bef" }} />
            </Box>
          )}
          {!loading && selection === "sortie-summary" && renderSortiePreview()}
          {!loading && (selection === "schedule-weekly" || selection === "schedule-custom") && renderSchedulePreview()}
          {!loading && (selection === "kml-date" || selection === "kml-mission") && renderKmlPreview()}
          {!loading && selection === "collection-decks" && renderCollectionDecksPreview()}
          {!loading && selection === "rsta-html" && renderRstaHtmlPreview()}
          {!loading && selection === "rsta-csv" && renderRstaCSVPreview()}
          {!loading && selection === "change-log" && renderChangeLogPreview()}
        </Box>
      </Box>
    </Box>
  );
}
